/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import MiniHStackedBarChart from "./MiniHStackedBarChart";
import { getHeaderTextStyle, hasLowNSize, calculateYAxisDomain, splitHierarchy, formatDataLabel, useYAxisWidth } from "../chartHelpers";
import { NSizeLabel } from "../../components/NSizeLabel";
import { EllipsisText } from "../../components/EllipsisText";
import { ChartContainer } from "../../components/ChartContainer";
import { AxisLabel } from "../../components/AxisLabel";
import { ChartFootnote } from "../../components/ChartFootnote";
import { useContainerHeight } from "src/components/Hooks/useContainerHeight";
import { useContainerWidth } from "src/components/Hooks/useContainerWidth";
import { chartConfig } from "../../chartConfig";
import ChartHeader from "../../components/ChartHeader";
import { ChartRenderedFromType, IHierarchyDef, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import ChartTabularView from "../../components/ChartTabularView";
import { RightAlignedEllipsisText } from "../../components/RightAlignedEllipsisText";
import { XAxisScale } from "../../components/XAxisScale";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { useScaledConfig } from "../../ChartScaleContext";
import { pxToRem } from "src/pages/Insights/transformer/dashboardUtils";
import ChartError from "../../ChartError";

/* -------------------- Types -------------------- */
type FlatColumn = {
	path: string[];
	key: string;
	wavesData: Record<string, any[]>;
	n_size?: { min: number; max: number } | "No Data";
};

type HeaderCell = {
	label: string;
	colSpan: number;
	levelIndex: number;
};

interface IHStackedBarChartProps {
	chartResponse: any;
	chartRules?: Record<string, any>;
	isAISummaryAvailable?: boolean;
	isChartView?: boolean;
	setIsChartView?: (value: boolean) => void;
	renderedFrom?: ChartRenderedFromType;
}

type CellType = "header" | "nsize" | "attrLabel" | "waveLabel" | "data";

interface ICellPosition {
	rowIndex: number;
	colIndex: number;
	waveIndex?: number;
	totalWaves: number;
	totalRows: number;
	totalCols: number;
}

interface IBorderConfig {
	borderTop?: string;
	borderBottom?: string;
	borderLeft?: string;
	borderRight?: string;
}

/* -------------------- Optimized Lazy Border Cache -------------------- */
const useBorderGetter = (colorsConfig: any, showWaveColumn: boolean, totalRows: number, totalCols: number, totalWaves: number, isEffectiveMultiTile: boolean) => {
	const cacheRef = useRef<Map<string, IBorderConfig>>(new Map());
	const borderConfigRef = useRef(createBorderConfig(colorsConfig));

	useEffect(() => {
		borderConfigRef.current = createBorderConfig(colorsConfig);
		cacheRef.current.clear();
	}, [colorsConfig]);

	const getBorders = useCallback(
		(cellType: CellType, rowIndex: number, colIndex: number, waveIndex: number): IBorderConfig => {
			const key = `${cellType}:${rowIndex}:${colIndex}:${waveIndex}:${showWaveColumn}`;

			const cached = cacheRef.current.get(key);
			if (cached !== undefined) return cached;

			const position: ICellPosition = {
				rowIndex,
				colIndex,
				waveIndex,
				totalWaves,
				totalRows,
				totalCols,
			};

			const borders = getCellBorders(cellType, position, borderConfigRef.current, showWaveColumn, isEffectiveMultiTile);
			cacheRef.current.set(key, borders);

			return borders;
		},
		[showWaveColumn, totalRows, totalCols, totalWaves],
	);

	return getBorders;
};

/* -------------------- Border Helper -------------------- */
const createBorderConfig = (colorsConfig: any) => ({
	solid: `1px solid ${colorsConfig.borders.solid}`,
	dashed: `1px dashed ${colorsConfig.borders.solid}`,
	light: `1px solid ${colorsConfig.borders.lightGray}`,
});

const getCellBorders = (
	cellType: CellType,
	position: ICellPosition,
	borderConfig: { solid: string; dashed: string; light: string },
	showWaveColumn: boolean,
	isEffectiveMultiTile: boolean,
): IBorderConfig => {
	const { rowIndex, waveIndex = 0, totalWaves, totalRows, colIndex } = position;

	const fixedColCount = showWaveColumn ? 2 : 1;
	const dataColIndex = colIndex - fixedColCount;
	const isLastWave = waveIndex === totalWaves - 1;
	const isFirstWave = waveIndex === 0;
	const isFirstDataCol = dataColIndex === 0;
	const isLastRow = rowIndex === totalRows - 1;

	switch (cellType) {
		case "header":
			return rowIndex === 0 ? { borderTop: borderConfig.solid, borderLeft: dataColIndex === 0 ? undefined : borderConfig.solid } : {};

		case "nsize":
			return {
				borderTop: borderConfig.solid,
				borderBottom: borderConfig.solid,
				borderLeft: dataColIndex === 0 ? undefined : borderConfig.solid,
			};

		case "attrLabel":
			return { borderRight: borderConfig.solid };

		case "waveLabel":
			return {
				borderRight: borderConfig.solid,
				borderTop: rowIndex === 0 && isFirstWave ? borderConfig.solid : undefined,
				borderBottom: isLastWave ? borderConfig.solid : undefined,
			};

		case "data":
			return {
				borderLeft: isFirstDataCol ? undefined : borderConfig.solid,
				borderBottom: undefined,
				// borderBottom: isLastWave ? (isLastRow ? borderConfig.solid : undefined) : undefined,
			};

		default:
			return {};
	}
};

/* -------------------- Other Helpers -------------------- */
const calculateColumnNSize = (wavesData: Record<string, any[]>): { min: number; max: number } | "No Data" => {
	let min = Number.MAX_SAFE_INTEGER;
	let max = Number.MIN_SAFE_INTEGER;
	let foundValidNumber = false;

	Object.values(wavesData).forEach((rows) => {
		if (Array.isArray(rows)) {
			rows.forEach((row) => {
				if (row.series && Array.isArray(row.series)) {
					row.series.forEach((s: any) => {
						const val = s.n_size;
						let parsedVal = NaN;
						if (typeof val === "number") {
							parsedVal = val;
						} else if (typeof val === "string" && val !== "No data" && val !== "No Data") {
							parsedVal = parseFloat(val);
						}

						if (!isNaN(parsedVal)) {
							if (parsedVal < min) min = parsedVal;
							if (parsedVal > max) max = parsedVal;
							foundValidNumber = true;
						}
					});
				}
			});
		}
	});

	return foundValidNumber ? { min, max } : "No Data";
};

const buildFlatColumns = (dataNode: any, currentPath: string[] = [], columnHierarchyDepth?: number): FlatColumn[] => {
	const shouldTreatAsLeaf = columnHierarchyDepth !== undefined && currentPath.length >= columnHierarchyDepth;

	if (Array.isArray(dataNode)) {
		const wavesData: Record<string, any[]> = {
			Total: dataNode.map((item: any) => ({
				xAxis: item.xAxis || "Total",
				series: item.series,
				n_size: item.n_size,
			})),
		};

		return [
			{
				path: currentPath,
				key: currentPath.join("|") || "Total",
				wavesData,
				n_size: calculateColumnNSize(wavesData),
			},
		];
	}

	const keys = Object.keys(dataNode);
	if (keys.length === 0) return [];

	// Check if all values are arrays (potential leaf)
	const allValuesAreArrays = Object.values(dataNode).every((val) => Array.isArray(val));

	// Only treat as leaf if:
	// 1. We've reached the column hierarchy depth, OR
	// 2. No column hierarchy depth specified AND all values are arrays
	if (shouldTreatAsLeaf && allValuesAreArrays) {
		return [
			{
				path: currentPath,
				key: currentPath.join("|") || "Total",
				wavesData: dataNode,
				n_size: calculateColumnNSize(dataNode),
			},
		];
	}

	let cols: FlatColumn[] = [];
	keys.forEach((key) => {
		cols = cols.concat(buildFlatColumns(dataNode[key], [...currentPath, key], columnHierarchyDepth));
	});
	return cols;
};

const buildHeaderRow = (columns: FlatColumn[], levelIndex: number): HeaderCell[] => {
	const headers: HeaderCell[] = [];
	if (columns.length === 0) return headers;

	let currentLabel = columns[0].path[levelIndex];
	let currentSpan = 0;
	let prevCol: FlatColumn | null = null;

	columns.forEach((col, i) => {
		const label = col.path[levelIndex];
		let parentChanged = false;
		if (levelIndex > 0 && prevCol) {
			for (let p = 0; p < levelIndex; p++) {
				if (col.path[p] !== prevCol.path[p]) {
					parentChanged = true;
					break;
				}
			}
		}
		if (i > 0 && (label !== currentLabel || parentChanged)) {
			headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
			currentLabel = label;
			currentSpan = 1;
		} else {
			currentSpan++;
		}
		prevCol = col;
	});
	headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
	return headers;
};

const extractMeta = (columns: FlatColumn[], rowHierarchy: IHierarchyDef | null) => {
	const attributesSet = new Set<string>();
	const wavesSet = new Set<string>();
	const dataMap = new Map<string, any[]>();

	columns.forEach((col) => {
		Object.entries(col.wavesData).forEach(([waveName, rows]) => {
			wavesSet.add(waveName);
			if (Array.isArray(rows)) {
				rows.forEach((row: any) => {
					attributesSet.add(row.xAxis);
					const key = `${row.xAxis}|${waveName}|${col.key}`;
					dataMap.set(key, row.series);
				});
			}
		});
	});

	const waves = rowHierarchy?.values ?? Array.from(wavesSet).sort();

	return {
		attributes: Array.from(attributesSet),
		waves,
		dataMap,
	};
};

/* -------------------- Main Component -------------------- */
const HStackedBarChart: React.FC<IHStackedBarChartProps> = ({ chartResponse, chartRules, isAISummaryAvailable = false, isChartView, setIsChartView, renderedFrom }) => {
	const layoutConfig = chartConfig.getLayoutConfig();
	const dimensionsConfig = chartConfig.getDimensionsConfig();
	const spacingConfig = chartConfig.getSpacingConfig();
	const colorsConfig = chartConfig.getColorsConfig();
	const statTestConfig: IStatTestConfig | null = chartResponse?.chart_config?.stat_test_config ?? null;
	const scaled = useScaledConfig();
	const apiData = chartResponse?.data?.[0]?.chart_data;
	const colors = chartResponse?.chart_config?.ser_color;
	// Determines if chart_data is missing/empty/invalid to render fallback UI
	const isDataEmpty = useMemo(() => {
		if (apiData === null || apiData === undefined) return true;
		if (Array.isArray(apiData) && apiData.length === 0) return true;
		if (typeof apiData !== "object") return true; // guard against primitives
		if (!Array.isArray(apiData) && Object.keys(apiData).length === 0) return true;
		return false;
	}, [apiData]);
	const hierarchyDef: IHierarchyDef[] = chartResponse?.filter_hierarchy || [];
	const hasHierarchy = hierarchyDef.length > 0;
	const { columnHierarchies, rowHierarchy } = useMemo(() => splitHierarchy(hierarchyDef), [hierarchyDef]);

	// Use optional chaining — chart_config itself may be missing
	const labelFormat = chartResponse?.chart_config?.dlabel_fmt;

	const [visibleSegments, setVisibleSegments] = useState<string[]>();
	const [expanded, setExpanded] = useState(false);
	const [recalcKey, setRecalcKey] = useState(0);

	const containerRef = useRef<HTMLDivElement>(null);
	const contentAreaRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const footnoteRef = useRef<HTMLDivElement>(null);
	const tableHeaderRef = useRef<HTMLDivElement>(null);
	const tableViewContainerRef = useRef<HTMLDivElement>(null);
	const xAxisRef = useRef<HTMLDivElement>(null);
	const containerWidth = useContainerWidth(containerRef);
	// NEW — stabilize height to prevent resize observer feedback loop
	const ASK_KURAL_MAX_CHART_HEIGHT = 150;
	const isRenderedFromAskKural = renderedFrom === ChartRenderedFromType.AskKural;
	const isRenderedFromAskKuralInline = renderedFrom === ChartRenderedFromType.AskKuralInline;

	const rawContentAreaHeight = useContainerHeight(contentAreaRef, recalcKey);
	const stableContentAreaHeightRef = useRef<number>(0);
	const measureCountRef = useRef<number>(0);

	// Prefer value_params order (backend-authoritative); fall back to Object.keys(colors).
	const seriesOrder = useMemo(() => {
		const vp: unknown[] = chartResponse?.value_params;
		if (Array.isArray(vp) && vp.length > 0) return vp.map(String);
		return colors ? Object.keys(colors) : [];
	}, [colors, chartResponse?.value_params]);

	const contentAreaHeight = useMemo(() => {
		const raw = rawContentAreaHeight || 0;
		const prev = stableContentAreaHeightRef.current;

		if (prev === 0) {
			stableContentAreaHeightRef.current = raw;
			measureCountRef.current = 1;
			return raw;
		}

		measureCountRef.current += 1;

		if ((isRenderedFromAskKuralInline || isRenderedFromAskKural) && measureCountRef.current > 2) {
			return prev;
		}

		if (Math.abs(raw - prev) > 5) {
			stableContentAreaHeightRef.current = raw;
			return raw;
		}

		return prev;
	}, [rawContentAreaHeight, isRenderedFromAskKuralInline]);

	const tableViewContainerHeight = useContainerHeight(tableViewContainerRef) || contentAreaHeight;

	const rawTableHeaderHeight = useContainerHeight(tableHeaderRef, recalcKey);
	const stableTableHeaderHeightRef = useRef<number>(0);

	/**
	 * Use the maximum observed table header height to prevent oscillation.
	 * When text wraps in narrow columns, the header height inflates.
	 * Taking the max ensures the chart always reserves enough space
	 * for the tallest header measurement, allowing wrapping without overflow.
	 */
	const tableHeaderHeight = useMemo(() => {
		const raw = rawTableHeaderHeight || 0;
		if (raw > stableTableHeaderHeightRef.current) {
			stableTableHeaderHeightRef.current = raw;
		}
		return stableTableHeaderHeightRef.current;
	}, [rawTableHeaderHeight]);

	const rawXAxisHeight = useContainerHeight(xAxisRef, recalcKey);
	const stableXAxisHeightRef = useRef<number>(0);

	/**
	 * Stabilize x-axis height with same high-water-mark pattern.
	 * The XAxisScale can shift height slightly across renders
	 * which feeds back into dynamicChartHeight calculations.
	 */
	const xAxisHeight = useMemo(() => {
		const raw = rawXAxisHeight || 0;
		if (raw > stableXAxisHeightRef.current) {
			stableXAxisHeightRef.current = raw;
		}
		return stableXAxisHeightRef.current;
	}, [rawXAxisHeight]);

	const { isMultiTileSlide } = useSelector((state: RootState) => state.quantDashboardSlice);
	const isEffectiveMultiTile = isMultiTileSlide || isRenderedFromAskKuralInline;

	useEffect(() => {
		// Guard against missing ser_color to avoid Object.keys(undefined) crash
		setVisibleSegments(Object.keys(colors ?? {}));
	}, [colors]);

	useEffect(() => {
		if (isChartView) {
			setRecalcKey((prev) => prev + 1);
			// Reset high-water-marks on view switch so fresh measurements are taken
			stableTableHeaderHeightRef.current = 0;
			stableXAxisHeightRef.current = 0;
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
		}
	}, [isChartView]);

	// One-shot recalc once the table header first reports a real height.
	// On initial paint, the header ref hasn't been measured yet, so
	// dynamicChartHeight is computed against a zero header and the bars
	// render at the wrong scale. Reset the contentArea stabilization and
	// bump recalcKey so every ref re-measures against the settled layout.
	const didRecalcAfterHeaderRef = useRef(false);
	useEffect(() => {
		if (didRecalcAfterHeaderRef.current) return;
		if (rawTableHeaderHeight > 0) {
			didRecalcAfterHeaderRef.current = true;
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		}
	}, [rawTableHeaderHeight]);

	// Skip hierarchy flattening when data is empty to avoid runtime errors
	const columns = useMemo(
		() => (isDataEmpty ? [] : buildFlatColumns(apiData, [], columnHierarchies?.length || 0)),
		[apiData, columnHierarchies?.length, isDataEmpty],
	);

	// Skip hasLowNSize when data is empty — it may not handle undefined/empty internally
	const showLowNSize = useMemo(() => (isDataEmpty ? false : hasLowNSize(apiData, { threshold: 30 })), [apiData, isDataEmpty]);

	const xAxisDomain = useMemo(() => {
		let stackTotal = 0;

		columns.forEach((col) => {
			Object.values(col.wavesData).forEach((rows) => {
				if (Array.isArray(rows)) {
					rows.forEach((row: any) => {
						if (row.series && Array.isArray(row.series)) {
							const rowSum = row.series.reduce((sum: number, s: any) => {
								return sum + (typeof s.value === "number" && isFinite(s.value) ? s.value : 0);
							}, 0);
							if (rowSum > stackTotal) stackTotal = rowSum;
						}
					});
				}
			});
		});

		return calculateYAxisDomain([], labelFormat, stackTotal);
	}, [columns, labelFormat]);

	const { attributes, waves, dataMap } = useMemo(() => extractMeta(columns, rowHierarchy), [columns, rowHierarchy]);

	const { width: resolvedAttrColWidth } = useYAxisWidth(attributes, containerWidth, {
		scaleFactor: scaled.scaleFactor,
		isMultiTile: isEffectiveMultiTile,
	});

	const waveColWidth = useMemo(() => {
		if (waves.length === 0) return dimensionsConfig.column.minWaveWidth;
		const maxLength = Math.max(...waves.map((wave) => wave.length));
		const estimatedTextWidth = maxLength * 6.5;
		const calculatedWidth = Math.ceil(estimatedTextWidth) + 30;
		return Math.max(dimensionsConfig.column.minWaveWidth, Math.min(calculatedWidth, dimensionsConfig.column.maxWaveWidth));
	}, [waves, dimensionsConfig.column.minWaveWidth, dimensionsConfig.column.maxWaveWidth]);

	const showWaveColumn = useMemo(() => {
		if (rowHierarchy) return true;
		return waves.length > 1 || (waves.length === 1 && waves[0] !== "Total");
	}, [rowHierarchy, waves]);

	const fixedColCount = showWaveColumn ? 2 : 1;

	const minContentWidth = useMemo(() => {
		const fixedColumnsWidth = showWaveColumn ? resolvedAttrColWidth + waveColWidth : resolvedAttrColWidth;
		return fixedColumnsWidth + columns.length * dimensionsConfig.column.minDataWidth;
	}, [showWaveColumn, resolvedAttrColWidth, waveColWidth, columns.length, dimensionsConfig.column.minDataWidth]);

	const needsScroll = useMemo(() => {
		if (containerWidth === 0) return false;
		return minContentWidth > containerWidth;
	}, [containerWidth, minContentWidth]);

	const dynamicChartHeight = useMemo(() => {
		if (!contentAreaHeight) {
			const fallbackHeight = isRenderedFromAskKuralInline ? ASK_KURAL_MAX_CHART_HEIGHT : dimensionsConfig.chart.defaultHeight;
			return fallbackHeight;
		}

		const resolvedHeaderHeight = tableHeaderHeight || 0;
		const resolvedXAxisHeight = isEffectiveMultiTile ? 0 : xAxisHeight || 32;

		const availableHeight = needsScroll
			? contentAreaHeight - resolvedHeaderHeight - resolvedXAxisHeight - 8 - 1
			: contentAreaHeight - resolvedHeaderHeight - resolvedXAxisHeight - 1;

		if (availableHeight < dimensionsConfig.chart.minHeight) {
			const constrainedHeight = Math.max(0, availableHeight);
			return constrainedHeight;
		}

		const maxHeight = isRenderedFromAskKuralInline ? ASK_KURAL_MAX_CHART_HEIGHT : dimensionsConfig.chart.maxHeight;
		const finalHeight = Math.min(maxHeight, availableHeight);

		return finalHeight;
	}, [contentAreaHeight, tableHeaderHeight, xAxisHeight, dimensionsConfig.chart, needsScroll, isEffectiveMultiTile, isRenderedFromAskKuralInline]);

	const rowCount = useMemo(() => {
		return showWaveColumn ? attributes.length * waves.length : attributes.length;
	}, [showWaveColumn, attributes.length, waves.length]);

	const rowHeight = useMemo(() => {
		if (!dynamicChartHeight || rowCount === 0) {
			return dimensionsConfig.row.minHeightStacked;
		}

		const availablePerRow = Math.max(0, dynamicChartHeight) / rowCount;

		// Always enforce the minimum row height — if rows don't fit,
		// needsYScroll will activate vertical scrolling instead of
		// compressing rows below the readable threshold
		return Math.max(dimensionsConfig.row.minHeightStacked, availablePerRow);
	}, [dynamicChartHeight, rowCount, dimensionsConfig.row.minHeightStacked]);

	// needsYScroll already handles this correctly — no change needed:
	const needsYScroll = useMemo(() => {
		if (!dynamicChartHeight || rowCount === 0) return false;
		const totalContentHeight = rowCount * rowHeight + (rowCount - 1) * spacingConfig.gap.row;
		return totalContentHeight > dynamicChartHeight;
	}, [dynamicChartHeight, rowCount, rowHeight, spacingConfig.gap.row]);

	const gridTemplate = useMemo(() => {
		const attrColumn = `${resolvedAttrColWidth}px`;
		const waveColumn = `${waveColWidth}px`;
		const frUnit = needsYScroll && !needsScroll ? `minmax(0, 1fr)` : `minmax(${dimensionsConfig.column.minDataWidth}px, 1fr)`;

		if (needsScroll) {
			return showWaveColumn
				? `${attrColumn} ${waveColumn} repeat(${columns.length}, minmax(${dimensionsConfig.column.minDataWidth}px, 1fr))`
				: `${attrColumn} repeat(${columns.length}, minmax(${dimensionsConfig.column.minDataWidth}px, 1fr))`;
		}

		return showWaveColumn ? `${attrColumn} ${waveColumn} repeat(${columns.length}, ${frUnit})` : `${attrColumn} repeat(${columns.length}, ${frUnit})`;
	}, [needsScroll, needsYScroll, showWaveColumn, columns.length, dimensionsConfig.column.minDataWidth, resolvedAttrColWidth, waveColWidth]);

	const SCROLLBAR_WIDTH = 6;

	const dataColumnWidth = useMemo(() => {
		if (containerWidth === 0 || columns.length === 0) return 0;

		const fixedColumnsWidth = showWaveColumn ? resolvedAttrColWidth + waveColWidth : resolvedAttrColWidth;
		const scrollbarOffset = needsYScroll ? SCROLLBAR_WIDTH : 0;
		const availableForData = containerWidth - fixedColumnsWidth - scrollbarOffset;

		if (needsScroll) return dimensionsConfig.column.minDataWidth;

		return Math.floor(availableForData / columns.length);
	}, [containerWidth, columns.length, showWaveColumn, resolvedAttrColWidth, waveColWidth, needsScroll, needsYScroll, dimensionsConfig.column.minDataWidth]);

	const getBorders = useBorderGetter(colorsConfig, showWaveColumn, attributes.length, columns.length, waves.length, isEffectiveMultiTile);

	if (isDataEmpty) {
		return <ChartError errorMessage="No data available" setChartData={() => { }} setIsChartLoading={() => { }} is_retry={false} />;
	}

	return (
		<ChartContainer innerRef={containerRef} scaledPadding={scaled.layout.containerPadding} renderedFrom={renderedFrom}>
			{/* HEADER: Title + Legend */}
			<div ref={headerRef}>
				<ChartHeader
					title={chartResponse.chart_title}
					ai_header={chartResponse?.ai_header}
					subtitle={chartResponse.chart_subtitle}
					legendWaves={chartResponse?.chart_config?.ser_color}
					visibleWaves={visibleSegments}
					setVisibleWaves={setVisibleSegments}
					order={chartResponse?.value_params}
					expanded={expanded}
					setExpanded={setExpanded}
					layout="center"
					isAISummaryAvailable={isAISummaryAvailable}
					isChartView={isChartView}
					setIsChartView={setIsChartView}
					showToggle={scaled.layout.showViewToggle}
					scaledTitleFontSize={pxToRem(scaled.typography.title)}
					scaledLegendFontSize={scaled.typography.legend}
					showSubtitle={scaled.layout.showSubtitle}
					showLegend={scaled.layout.showLegend}
					legendLines={scaled.layout.legendLines}
					renderedFrom={renderedFrom}
				/>
			</div>

			{isChartView ? (
				<div ref={contentAreaRef} className="flex flex-1 min-h-0">
					{isChartView && chartResponse?.y_axis_label && !isEffectiveMultiTile && (
						<AxisLabel
							label={chartResponse?.y_axis_label || chartResponse?.chart_config?.y_lbl || "Attributes"}
							orientation="vertical"
							maxHeight={contentAreaHeight || undefined}
						/>
					)}

					<div className={`flex-1 relative ${needsScroll ? "overflow-x-auto" : "overflow-x-hidden"} overflow-y-auto`}>
						<div
							style={{
								minWidth: needsScroll ? `${minContentWidth}px` : undefined,
								width: "100%",
							}}>
							<div ref={tableHeaderRef} className="sticky top-0 z-[1000]">
								{!hasHierarchy ? (
									<div
										className="grid sticky z-30 bg-white"
										style={{
											gridTemplateColumns: gridTemplate,
											top: 0,
											height: pxToRem(scaled.layout.nSizeRowHeight),
											width: "100%",
										}}>
										<div className="sticky left-0 bg-white z-40" style={{ borderRight: `1px solid ${colorsConfig.borders.gray}` }} />
										{showWaveColumn && (
											<div
												className="sticky bg-white z-40"
												style={{
													left: `${resolvedAttrColWidth}px`,
													borderRight: `1px solid ${colorsConfig.borders.gray}`,
													borderBottom: `1px solid ${colorsConfig.borders.gray}`,
												}}
											/>
										)}

										{columns.map((col, idx) => {
											const borders = getBorders("nsize", 0, idx + fixedColCount, 0);
											return (
												<div key={idx} className="flex flex-col justify-center items-center text-center bg-white" style={borders}>
													<NSizeLabel nSize={col.n_size} fontSize={scaled.typography.nSize} />
												</div>
											);
										})}
									</div>
								) : (
									<>
									{columnHierarchies?.map((levelDef: IHierarchyDef, levelIndex: number) => {
										const headerCells = buildHeaderRow(columns, levelIndex);
										const isLastLevel = levelIndex === hierarchyDef.length - 1;

									return (
												<div
													key={levelIndex}
													className="grid bg-white"
													style={{
														gridTemplateColumns: gridTemplate,
														minHeight: pxToRem(scaled.layout.headerRowHeight),
														width: "100%",
													}}>
													<div
														className="sticky left-0 bg-white z-40"
														style={{
															borderRight: showWaveColumn ? undefined : `1px solid ${colorsConfig.borders.gray}`,
														}}
													/>
													{showWaveColumn && (
														<div
															className="sticky bg-white z-40"
															style={{
																left: `${resolvedAttrColWidth}px`,
																borderRight: `1px solid ${colorsConfig.borders.gray}`,
															}}
														/>
													)}

													{headerCells.map((cell, idx) => {
														let colIndex = 0;
														for (let k = 0; k < idx; k++) colIndex += headerCells[k].colSpan;

														const borders = getBorders("header", 0, colIndex + fixedColCount, 0);
														const textStyle = getHeaderTextStyle(levelIndex, isLastLevel, scaled.scaleFactor);

														return (
															<div
																key={`${levelIndex}-${idx}`}
																className={`flex flex-col justify-center items-center text-center bg-white px-2 ${textStyle.className}`}
																style={{
																	gridColumn: `span ${cell.colSpan}`,
																	...borders,
																}}>
																<EllipsisText
																	text={cell.label}
																	maxLines={scaled.layout.maxAttrLabelLines}
																	lineHeight={textStyle.lineHeight}
																	fontSize={textStyle.fontSize}
																	className="bg-white"
																/>
															</div>
														);
													})}
												</div>
											);
										})}

										<div
											className="grid bg-white"
											style={{
												gridTemplateColumns: gridTemplate,
												height: pxToRem(scaled.layout.nSizeRowHeight),
												width: "100%",
											}}>
											<div
												className="sticky left-0 bg-white z-40"
												style={{
													borderRight: showWaveColumn ? undefined : `1px solid ${colorsConfig.borders.gray}`,
												}}
											/>
											{showWaveColumn && (
												<div
													className="sticky bg-white z-40"
													style={{
														left: `${resolvedAttrColWidth}px`,
														borderRight: `1px solid ${colorsConfig.borders.gray}`,
														borderBottom: `1px solid ${colorsConfig.borders.gray}`,
													}}
												/>
											)}

											{columns.map((col, idx) => {
												const borders = getBorders("nsize", 0, idx + fixedColCount, 0);
												return (
													<div key={`nsize-${idx}`} className="flex flex-col justify-center items-center text-center bg-white" style={borders}>
														<NSizeLabel nSize={col.n_size} fontSize={scaled.typography.nSize} />
													</div>
												);
											})}
										</div>
									</>
								)}
							</div>

						{/* Body Rows */}
						<div style={{
							minHeight: isRenderedFromAskKuralInline ? undefined : `${dynamicChartHeight}px`,
							maxHeight: isRenderedFromAskKuralInline ? `${dynamicChartHeight}px` : undefined,
							height: isRenderedFromAskKuralInline ? `${dynamicChartHeight}px` : undefined,
						}}>
								{attributes.map((attr, rowIndex) => {
									const isAlt = rowIndex % 2 === 1;
									const bgColor = isAlt ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
									const attrBorders = getBorders("attrLabel", rowIndex, 0, 0);
									const cellPaddingX = isEffectiveMultiTile ? 4 : 16;
									const labelTextWidth = resolvedAttrColWidth - cellPaddingX * 2;

									return (
										<div
											key={attr}
											className="grid"
											style={{
												gridTemplateColumns: gridTemplate,
												backgroundColor: bgColor,
											}}>
											<div
												className={`${isEffectiveMultiTile ? "px-1" : "px-4"} py-1 font-normal sticky left-0 z-20 flex items-center justify-end`}
												style={{
													color: colorsConfig.text.primary,
													backgroundColor: bgColor,
													height: showWaveColumn ? undefined : rowHeight,
													fontSize: pxToRem(scaled.typography.yAxisLabel),
													...attrBorders,
												}}>
												<RightAlignedEllipsisText
													text={attr}
													maxLines={scaled.layout.maxAttrLabelLines}
													lineHeight={scaled.typography.yAxisLabel + 4}
													className="text-end"
													maxWidth={labelTextWidth}
												/>
											</div>

											{showWaveColumn && (
												<div
													className="flex flex-col sticky z-20"
													style={{
														left: `${resolvedAttrColWidth}px`,
														backgroundColor: bgColor,
													}}>
													{waves.map((wave, wIdx) => {
														const waveBgColor =
															(waves.length % 2 === 0 ? wIdx : rowIndex + wIdx) % 2 === 0
																? colorsConfig.backgrounds.white
																: colorsConfig.backgrounds.alternate;
														const waveBorders = getBorders("waveLabel", rowIndex, 1, wIdx);

														return (
															<div
																key={wave}
																className="flex items-center justify-center font-normal px-2"
																style={{
																	height: rowHeight,
																	color: colorsConfig.text.primary,
																	backgroundColor: waveBgColor,
																	fontSize: pxToRem(scaled.typography.yAxisLabel),
																	...waveBorders,
																}}>
																<RightAlignedEllipsisText
																	text={wave}
																	maxLines={3}
																	lineHeight={scaled.typography.yAxisLabel + 4}
																	className="text-end"
																/>
															</div>
														);
													})}
												</div>
											)}

											{columns.map((col, colIdx) => {
												const dataBorders = getBorders("data", rowIndex, colIdx + fixedColCount, 0);

												if (!showWaveColumn) {
													const wave = waves[0] || "Total";
													const fullKey = `${attr}|${wave}|${col.key}`;
													const fullSeries = dataMap.get(fullKey);
													const filteredSeries = fullSeries?.filter((s) => visibleSegments?.includes(s.name));

													return (
														<div
															key={`${attr}-${colIdx}`}
															className="py-[2px] flex items-center justify-center overflow-hidden"
															style={{
																height: rowHeight * waves.length,
																backgroundColor: bgColor,
																...dataBorders,
															}}>
															{filteredSeries && filteredSeries.length > 0 ? (
																<MiniHStackedBarChart
																	key={filteredSeries.map((s: any) => s.value).join("-")}
																	series={filteredSeries}
																	colors={colors}
																	labelFormat={chartResponse.chart_config.dlabel_fmt}
																	xAxisDomain={xAxisDomain}
																	rowHeight={rowHeight * waves.length}
																	containerWidth={dataColumnWidth}
																	chartHeight={dynamicChartHeight}
																	statTestConfig={statTestConfig}
																	renderedFrom={renderedFrom}
																/>
															) : (
																<span className="text-xs" style={{ color: colorsConfig.text.muted }}>
																	-
																</span>
															)}
														</div>
													);
												}

												return (
													<div key={`${attr}-${colIdx}`} className="flex flex-col">
														{waves.map((wave, wIdx) => {
															const fullKey = `${attr}|${wave}|${col.key}`;
															const fullSeries = dataMap.get(fullKey);
															const filteredSeries = fullSeries?.filter((s) => visibleSegments?.includes(s.name));
															const waveBgColor =
																(waves.length % 2 === 0 ? wIdx : rowIndex + wIdx) % 2 === 0
																	? colorsConfig.backgrounds.white
																	: colorsConfig.backgrounds.alternate;
															const waveDataBorders = getBorders("data", rowIndex, colIdx + fixedColCount, wIdx);

															return (
																<div
																	key={wave}
																	className="py-[2px] flex items-center justify-center overflow-hidden"
																	style={{
																		height: rowHeight,
																		backgroundColor: waveBgColor,
																		...waveDataBorders,
																	}}>
																	{filteredSeries && filteredSeries.length > 0 ? (
																		<MiniHStackedBarChart
																			key={filteredSeries.map((s: any) => s.value).join("-")}
																			series={filteredSeries}
																			colors={colors}
																			labelFormat={chartResponse.chart_config.dlabel_fmt}
																			xAxisDomain={xAxisDomain}
																			rowHeight={rowHeight}
																			containerWidth={dataColumnWidth}
																			chartHeight={dynamicChartHeight}
																			statTestConfig={statTestConfig}
																		/>
																	) : (
																		<span className="text-xs" style={{ color: colorsConfig.text.muted }}>
																			-
																		</span>
																	)}
																</div>
															);
														})}
													</div>
												);
											})}
										</div>
									);
								})}
							</div>

							{/* X-Axis Scale Row */}
							<div
								ref={xAxisRef}
								className="grid sticky bottom-0 z-[100] bg-white"
								style={{ gridTemplateColumns: gridTemplate, height: xAxisHeight ? `${xAxisHeight}px` : undefined }}>
								<div className="sticky left-0 z-[200] bg-white" />
								{showWaveColumn && <div className="sticky z-[200] bg-white" style={{ left: `${resolvedAttrColWidth}px` }} />}
								{columns.map((_, idx) => (
									<div key={`xaxis-${idx}`} style={{ borderTop: `1px solid ${colorsConfig.borders.solid}` }}>
										{(!isEffectiveMultiTile || isRenderedFromAskKural) && (
											<XAxisScale
												paddingLeft={0}
												paddingRight={layoutConfig.grid.right / 2}
												domain={xAxisDomain}
												labelFormat={labelFormat}
												formatDataLabel={(a, b, c) => formatDataLabel(a, b, c, false)}
											tickCount={2}
											ticksCentered={false}
											/>
										)}
									</div>
								))}
							</div>
						</div>
					</div>
					<div style={{ height: `calc(100% - ${xAxisHeight + (needsScroll ? 6 : 0)}px)`, borderRight: `1px solid ${colorsConfig.borders.solid}` }} />
				</div>
			) : (
				<div
					ref={tableViewContainerRef}
					style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", justifyContent: "center", alignItems: "center" }}>
					<ChartTabularView
						chartData={apiData}
						attributes={attributes}
						chartType="h_stacked_bar"
						tableContainerHeight={tableViewContainerHeight}
						parentContainerWidth={containerWidth}
						labelFormat={chartResponse?.chart_config?.dlabel_fmt}
						statTestConfig={statTestConfig}
						renderedFrom={renderedFrom}
						filterHierarchy={hierarchyDef}
						seriesOrder={seriesOrder}
					/>
				</div>
			)}

			{isChartView && chartResponse?.x_axis_label && !isEffectiveMultiTile && (
				<AxisLabel label={chartResponse?.chart_config?.x_lbl || chartResponse?.x_axis_label} orientation="horizontal" className="mt-2" />
			)}

			<div ref={footnoteRef}>
				<ChartFootnote
					src={chartResponse.footnote?.src}
					footnoteObj={chartResponse.footnote}
					analysis_name={chartResponse.analysis_type}
					hasLowNSize={showLowNSize}
					isWeighted={chartResponse.is_weight}
					className="py-2 mt-auto"
					stat_message={chartResponse.footnote?.stat_message}
					valueParams={chartResponse?.value_params}
					renderedFrom={renderedFrom}
				/>
			</div>
		</ChartContainer>
	);
};

export default HStackedBarChart;
